import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, FileText, ExternalLink, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Live table of every article shipped via the batch seed pipeline.
 * Columns: slug, title, category, featured_order, view_count, last_updated,
 *          publish status, "Rebuild from asset" action.
 */
export default function SeededArticlesTab() {
  const token = sessionStorage.getItem('admin_token');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rebuilding, setRebuilding] = useState({}); // slug → true while POSTing
  const [lastAction, setLastAction] = useState(null); // { slug, ok, msg }

  const fetchRows = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/seeded-articles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setRows(data.articles || []);
    } catch (err) {
      console.error('Fetch seeded articles failed:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const rebuild = async (slug) => {
    setRebuilding(p => ({ ...p, [slug]: true }));
    setLastAction(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/seeded-articles/${slug}/rebuild`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || `HTTP ${res.status}`);
      setLastAction({ slug, ok: true, msg: `Rebuilt · ${data.content_length.toLocaleString()} chars` });
      await fetchRows();
    } catch (err) {
      console.error('Rebuild failed:', err);
      setLastAction({ slug, ok: false, msg: err.message });
    } finally {
      setRebuilding(p => ({ ...p, [slug]: false }));
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-4" data-testid="seeded-articles-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Seeded Articles</h2>
          <p className="text-xs text-gray-500 mt-0.5">Every article shipped via the batch seed pipeline. Click &lsquo;Rebuild&rsquo; to refresh a single article from its asset on disk.</p>
        </div>
        <button
          onClick={fetchRows}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          data-testid="seeded-refresh-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {lastAction && (
        <div
          className={`text-xs flex items-center gap-2 px-3 py-2 rounded-lg border ${
            lastAction.ok
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
          data-testid="seeded-action-banner"
        >
          {lastAction.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
          <span className="font-medium">{lastAction.slug}</span>
          <span>·</span>
          <span>{lastAction.msg}</span>
        </div>
      )}

      <div className="bg-white border border-gray-100 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Article</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Category</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">Featured #</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">Views</th>
              <th className="text-left px-3 py-2 font-medium text-gray-600">Last Updated</th>
              <th className="text-center px-3 py-2 font-medium text-gray-600">Status</th>
              <th className="text-right px-3 py-2 font-medium text-gray-600">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan="7" className="text-center py-8 text-gray-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="7" className="text-center py-8 text-gray-400">No seeded articles found. Check that seed modules import correctly on the backend.</td></tr>
            )}
            {rows.map(row => (
              <tr key={row.slug} className="border-t border-gray-50 hover:bg-gray-50/50" data-testid={`seeded-row-${row.slug}`}>
                <td className="px-3 py-3">
                  <div className="flex items-start gap-2">
                    <FileText className="w-3.5 h-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <a
                        href={`/blogs/article/${row.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-900 font-medium hover:text-teal-600 inline-flex items-center gap-1"
                      >
                        <span className="truncate max-w-xs" title={row.title}>{row.title}</span>
                        <ExternalLink className="w-3 h-3 opacity-60" />
                      </a>
                      <div className="text-[10px] text-gray-400 mt-0.5">
                        {row.slug} · seed: {row.seed_source}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-600">{row.category || '—'}</td>
                <td className="px-3 py-3 text-right font-mono text-gray-700">{row.featured_order ?? '—'}</td>
                <td className="px-3 py-3 text-right font-mono text-gray-700">{(row.view_count || 0).toLocaleString()}</td>
                <td className="px-3 py-3 text-gray-500">{fmtDate(row.last_updated)}</td>
                <td className="px-3 py-3 text-center">
                  {row.in_db ? (
                    row.published ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-700 text-[10px] font-medium">
                        <CheckCircle2 className="w-3 h-3" /> Live
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-800 text-[10px] font-medium">
                        Draft
                      </span>
                    )
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-medium">
                      Not in DB
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-right">
                  <button
                    onClick={() => rebuild(row.slug)}
                    disabled={!!rebuilding[row.slug]}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-gray-700 border border-gray-200 rounded-md hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid={`seeded-rebuild-${row.slug}`}
                  >
                    {rebuilding[row.slug] ? (
                      <><Loader2 className="w-3 h-3 animate-spin" /> Rebuilding…</>
                    ) : (
                      <><RefreshCw className="w-3 h-3" /> Rebuild</>
                    )}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-gray-400">
        Total: {rows.length} article{rows.length === 1 ? '' : 's'} · Rebuilds are idempotent and preserve `id`, `_id`, `view_count`, and `created_at`.
      </div>
    </div>
  );
}
